import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast, Toaster } from "sonner";
import { BadgeCheck, Clock, Video } from "lucide-react";

export default function DoctorDashboard() {
    const [profile, setProfile] = useState(null);
    const [appts, setAppts] = useState([]);
    const [edit, setEdit] = useState(false);
    const [f, setF] = useState({});
    const [s, setS] = useState({ doctor_revenue_share: 0.7 });

    const load = async () => {
        try {
            const [p, a, st] = await Promise.all([
                api.get("/doctors/me"),
                api.get("/appointments/doctor"),
                api.get("/settings/public"),
            ]);
            setProfile(p.data); setF(p.data); setAppts(a.data); setS(st.data);
        } catch {}
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const { data } = await api.put("/doctors/me", f);
            setProfile(data); setEdit(false);
            toast.success("Profile updated");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    };

    if (!profile) return <div className="max-w-5xl mx-auto py-20 text-center text-stone-500">Loading…</div>;

    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="doctor-dashboard">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="label-eyebrow text-sky-700">Doctor dashboard</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Welcome, {profile.name}</h1>
                </div>
                <span className={`text-xs uppercase tracking-widest px-3 py-1 border ${profile.status === "approved" ? "border-sky-700 text-sky-700" : profile.status === "pending" ? "border-amber-600 text-amber-700" : "border-red-600 text-red-700"}`} data-testid="status-badge">
                    {profile.status === "approved" && <BadgeCheck className="w-3 h-3 inline mr-1" />}
                    {profile.status === "pending" && <Clock className="w-3 h-3 inline mr-1" />}
                    {profile.status}
                </span>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white border border-stone-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-serif text-2xl">Profile</h2>
                        {!edit ? (
                            <Button onClick={() => setEdit(true)} variant="outline" className="rounded-none border-stone-400" data-testid="edit-profile">Edit</Button>
                        ) : (
                            <div className="flex gap-2">
                                <Button onClick={() => { setF(profile); setEdit(false); }} variant="ghost" className="rounded-none">Cancel</Button>
                                <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="save-profile">Save</Button>
                            </div>
                        )}
                    </div>
                    {edit ? (
                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <F label="Phone" v={f.phone} on={set("phone")} />
                                <F label="Qualification" v={f.qualification} on={set("qualification")} />
                                <F label="Specialization" v={f.specialization} on={set("specialization")} />
                                <F label="Experience (yrs)" v={f.experience_years} on={set("experience_years")} type="number" />
                                <F label="City" v={f.city} on={set("city")} />
                                <F label="State" v={f.state} on={set("state")} />
                                <F label="Clinic name" v={f.clinic_name} on={set("clinic_name")} />
                                <F label="Photo URL" v={f.photo_url} on={set("photo_url")} />
                            </div>
                            <F label="Clinic address" v={f.clinic_address} on={set("clinic_address")} />
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Bio</Label>
                                <Textarea value={f.bio || ""} onChange={set("bio")} className="rounded-none border-stone-300 mt-1" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Online consults</span><Switch checked={!!f.online_consult} onCheckedChange={(v) => setF((p) => ({ ...p, online_consult: v }))} /></label>
                                <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Offline consults</span><Switch checked={!!f.offline_consult} onCheckedChange={(v) => setF((p) => ({ ...p, offline_consult: v }))} /></label>
                            </div>
                            <div className="bg-sky-50 border border-sky-200 p-4">
                                <div className="text-[10px] uppercase tracking-widest text-sky-800 mb-2">Your consultation fees (₹)</div>
                                <p className="text-xs text-stone-600 mb-3">Set your own fee for each mode. Hahnemann's commission is {Math.round((1 - (s.doctor_revenue_share || 0.7)) * 100)}% — you keep <strong>{Math.round((s.doctor_revenue_share || 0.7) * 100)}%</strong> of every consultation.</p>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <F
                                        label="Online consult fee"
                                        v={f.fee_online ?? ""}
                                        on={(e) => setF((p) => ({ ...p, fee_online: e?.target?.value === "" ? null : parseFloat(e.target.value) }))}
                                        type="number"
                                        placeholder={`Default ₹${s.consultation_fee_online || 500}`}
                                        testId="doc-fee-online"
                                    />
                                    <F
                                        label="Offline consult fee"
                                        v={f.fee_offline ?? ""}
                                        on={(e) => setF((p) => ({ ...p, fee_offline: e?.target?.value === "" ? null : parseFloat(e.target.value) }))}
                                        type="number"
                                        placeholder={`Default ₹${s.consultation_fee_offline || 700}`}
                                        testId="doc-fee-offline"
                                    />
                                </div>
                                <p className="text-[10px] text-stone-500 mt-2">Leave blank to use HPL's default fee. Doctor's contact details are kept private from patients until they pay.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <Info l="Email" v={profile.email} />
                            <Info l="Phone" v={profile.phone} />
                            <Info l="Qualification" v={profile.qualification} />
                            <Info l="Specialization" v={profile.specialization} />
                            <Info l="Experience" v={`${profile.experience_years || 0} years`} />
                            <Info l="City" v={`${profile.city}${profile.state ? ", " + profile.state : ""}`} />
                            <Info l="Online consults" v={profile.online_consult ? "Yes" : "No"} />
                            <Info l="Offline consults" v={profile.offline_consult ? "Yes" : "No"} />
                            <Info l="Online fee" v={profile.fee_online != null ? `₹${profile.fee_online}` : `Default (₹${s.consultation_fee_online || 500})`} />
                            <Info l="Offline fee" v={profile.fee_offline != null ? `₹${profile.fee_offline}` : `Default (₹${s.consultation_fee_offline || 700})`} />
                        </div>
                    )}
                </div>
                <aside className="bg-sky-50 border border-sky-700 p-6">
                    <div className="label-eyebrow text-sky-700">Earnings</div>
                    <div className="font-serif text-2xl text-stone-900 mt-2">You receive {Math.round((s.doctor_revenue_share || 0.7) * 100)}%</div>
                    <p className="text-xs text-stone-700 mt-2">Hahnemann sets the consultation fees and retains {Math.round((1 - (s.doctor_revenue_share || 0.7)) * 100)}%.</p>
                    <div className="mt-4 text-sm space-y-1 text-stone-700">
                        <div>Total appointments: <span className="font-medium">{appts.length}</span></div>
                        <div>Total earnings: <span className="font-medium text-sky-700">{formatINR(appts.reduce((s, a) => s + (a.fee_doctor || 0), 0))}</span></div>
                    </div>
                </aside>
            </div>

            <h2 className="font-serif text-3xl mt-12 mb-4">Appointments</h2>
            {appts.length === 0 ? (
                <div className="bg-white border border-stone-200 p-8 text-center text-sm text-stone-500">No appointments booked yet.</div>
            ) : (
                <div className="bg-white border border-stone-200">
                    {appts.map((a) => (
                        <div key={a.id} className="border-b border-stone-100 last:border-b-0 p-4 grid grid-cols-6 gap-2 items-center text-sm" data-testid={`doc-appt-${a.id}`}>
                            <div className="col-span-2"><div className="font-medium">{a.user_name}</div><div className="text-xs text-stone-500">{a.user_email}</div></div>
                            <div>{a.date} · {a.time_slot}</div>
                            <div className="uppercase text-xs tracking-widest">{a.mode}</div>
                            <div className="text-right text-sky-700 font-medium">{formatINR(a.fee_doctor)}</div>
                            <div className="text-right">
                                {a.mode === "online" && a.payment_status === "paid" && (
                                    <Link to={`/video-call/${a.id}`} className="inline-flex items-center gap-1 px-2 py-1 bg-sky-700 hover:bg-sky-800 text-stone-50 text-xs" data-testid={`doc-join-${a.id}`}>
                                        <Video className="w-3 h-3" />Start
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const F = ({ label, v, on, type = "text", placeholder = "", testId }) => (
    <div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input type={type} value={v ?? ""} onChange={on} placeholder={placeholder} className="rounded-none border-stone-300 mt-1" data-testid={testId} /></div>
);
const Info = ({ l, v }) => (<div><div className="text-[10px] uppercase tracking-widest text-stone-500">{l}</div><div className="text-stone-900">{v}</div></div>);
